import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, ErrorBanner, Field, Input, PasswordInput } from "@/components/ui-kit";
import { BankSelect } from "@/components/BankSelect";
import { useAuth, formatNaira } from "@/lib/auth";
import { useNigerianBanks } from "@/hooks/use-nigerian-banks";
import {
  createLoanApplication,
  fetchPrimaryBankAccount,
  filterExistingKycDocs,
  isKycIdentifierTaken,
  saveDisbursementBankAccount,
  submitKyc,
  uploadKycDocNow,
  verifyBankAccount,
  type UploadedKycDoc,
} from "@/lib/data";
import { formatDate } from "@/lib/format";
import { unlockPdfFileIfNeeded } from "@/lib/pdf-unlock";
import {
  EXPRESS_DOC_TYPES,
  EXPRESS_OPTIONAL_DOC_TYPES,
  MAX_LOAN_DAYS,
  MIN_LOAN_DAYS,
  NOK_RELATIONSHIPS,
  TIER_LABEL,
  VERIFIED_EXTRA_DOC_TYPES,
  canNegotiateRates,
  clampLoanDaysForLevel,
  formatDurationDays,
  isVerifiedUpgradePending,
  maxLoanDaysForLevel,
  type VerificationLevel,
} from "@/lib/types";
import { expressRepayAmount } from "@/lib/express-rates";

export const Route = createFileRoute("/borrower/apply")({
  validateSearch: (search: Record<string, unknown>) => ({
    flow: search.flow === "kyc" ? ("kyc" as const) : ("loan" as const),
  }),
  component: ApplyPage,
});

/** 0 About you · 1 Express docs · 2 Tier choice · 3 Verified extras · 4 Review */
type Step = 0 | 1 | 2 | 3 | 4;
type Tier = "express" | "verified";

const KYC_STEPS = ["About you", "Documents", "Tier", "Review"];

/** Local YYYY-MM-DD, noon to avoid DST off-by-one. */
function localDatePlusDays(from: Date, days: number): string {
  const d = new Date(from);
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysBetweenTodayAnd(dateStr: string): number {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const target = new Date(`${dateStr}T12:00:00`);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

function stepperIndex(s: Step): number {
  if (s <= 1) return s;
  if (s <= 3) return 2;
  return 3;
}

const DOC_TYPES = [...EXPRESS_DOC_TYPES, ...EXPRESS_OPTIONAL_DOC_TYPES, ...VERIFIED_EXTRA_DOC_TYPES] as const;

type DocType = (typeof DOC_TYPES)[number];
type DocMap = Partial<Record<DocType, UploadedKycDoc>>;
type UploadStatus = "idle" | "uploading" | "done" | "error";

const selectClass =
  "w-full bg-input/60 border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20 transition";
const textareaClass =
  "w-full min-h-[96px] bg-input/60 border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20 transition resize-y";

type DraftFields = {
  step: Step;
  targetTier: Tier | null;
  dateOfBirth: string;
  houseAddress: string;
  officeAddress: string;
  workplaceName: string;
  salaryAmount: string;
  socialInstagram: string;
  socialFacebook: string;
  socialTwitter: string;
  socialYoutube: string;
  loanPurpose: string;
  repaymentPlan: string;
  bvn: string;
  nin: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  nokName: string;
  nokRelationship: string;
  nokPhone: string;
  nokAddress: string;
  guarantorName: string;
  guarantorRelationship: string;
  guarantorPhone: string;
  guarantorAddress: string;
  docs: DocMap;
  consentTerms: boolean;
  consentTruth: boolean;
};

function draftKey(userId: string) {
  return `crediking_kyc_draft_${userId}`;
}

/** Map DB / legacy document types into the form DocType keys. */
function normalizeDocType(raw: string): DocType | null {
  const t = raw === "id_front" ? "nin_front" : raw;
  return (DOC_TYPES as readonly string[]).includes(t) ? (t as DocType) : null;
}

function docsFromApprovedKyc(documents: { type: string; fileName: string; storagePath: string }[] | undefined): DocMap {
  const out: DocMap = {};
  for (const d of documents ?? []) {
    const type = normalizeDocType(d.type);
    if (!type || !d.storagePath) continue;
    // Prefer explicit nin_front over legacy id_front alias if both exist
    if (out[type] && d.type !== type) continue;
    out[type] = { type, path: d.storagePath, name: d.fileName || type };
  }
  return out;
}


function loadDraft(userId: string): DraftFields | null {
  try {
    const raw = localStorage.getItem(draftKey(userId));
    if (!raw) return null;
    return JSON.parse(raw) as DraftFields;
  } catch {
    return null;
  }
}

function saveDraft(userId: string, data: DraftFields) {
  try {
    localStorage.setItem(draftKey(userId), JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

function clearDraft(userId: string) {
  try {
    localStorage.removeItem(draftKey(userId));
  } catch {
    /* ignore */
  }
}

function ApplyPage() {
  const { session, refreshBorrower } = useAuth();
  const { banks: nigerianBanks, loading: banksLoading } = useNigerianBanks();
  const nav = useNavigate();
  const { flow } = Route.useSearch();
  const borrower = session?.kind === "borrower" ? session.borrower : null;
  const draftLoaded = useRef(false);

  /** Express-approved borrowers opening the KYC flow can upgrade to Verified. */
  const isVerifiedUpgrade = Boolean(
    borrower && borrower.kycStatus === "approved" && borrower.verificationLevel === "express",
  );

  const [draftReady, setDraftReady] = useState(
    Boolean(borrower?.kycStatus === "approved" && !isVerifiedUpgrade),
  );

  const startStep: Step = isVerifiedUpgrade ? 3 : borrower?.kycStatus === "approved" ? 4 : 0;
  const [step, setStep] = useState<Step>(startStep);
  const [targetTier, setTargetTier] = useState<Tier | null>(isVerifiedUpgrade ? "verified" : null);

  const [dateOfBirth, setDateOfBirth] = useState(borrower?.kyc?.dateOfBirth ?? "");
  const [houseAddress, setHouseAddress] = useState(borrower?.kyc?.houseAddress ?? "");
  const [officeAddress, setOfficeAddress] = useState(borrower?.kyc?.officeAddress ?? "");
  const [workplaceName, setWorkplaceName] = useState(borrower?.kyc?.workplaceName ?? "");
  const [salaryAmount, setSalaryAmount] = useState(
    borrower?.kyc?.salaryAmount != null ? String(borrower.kyc.salaryAmount) : "",
  );

  const [socialInstagram, setSocialInstagram] = useState(borrower?.kyc?.socialInstagram ?? "");
  const [socialFacebook, setSocialFacebook] = useState(borrower?.kyc?.socialFacebook ?? "");
  const [socialTwitter, setSocialTwitter] = useState(borrower?.kyc?.socialTwitter ?? "");
  const [socialYoutube, setSocialYoutube] = useState(borrower?.kyc?.socialYoutube ?? "");

  const [loanPurpose, setLoanPurpose] = useState(borrower?.kyc?.loanPurpose ?? "");
  const [repaymentPlan, setRepaymentPlan] = useState(borrower?.kyc?.repaymentPlan ?? "");

  const [bvn, setBvn] = useState(borrower?.kyc?.bvn ?? "");
  const [nin, setNin] = useState(borrower?.kyc?.nin ?? "");
  const [bvnStatus, setBvnStatus] = useState<"idle" | "checking" | "ok" | "taken">("idle");
  const [ninStatus, setNinStatus] = useState<"idle" | "checking" | "ok" | "taken">("idle");
  const [docs, setDocs] = useState<DocMap>({});
  const [uploadStatus, setUploadStatus] = useState<Partial<Record<DocType, UploadStatus>>>({});
  const [uploadErrors, setUploadErrors] = useState<Partial<Record<DocType, string>>>({});
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [verifyingBank, setVerifyingBank] = useState(false);

  const [nokName, setNokName] = useState("");
  const [nokRelationship, setNokRelationship] = useState("");
  const [nokPhone, setNokPhone] = useState("");
  const [nokAddress, setNokAddress] = useState("");

  const [guarantorName, setGuarantorName] = useState("");
  const [guarantorRelationship, setGuarantorRelationship] = useState("");
  const [guarantorPhone, setGuarantorPhone] = useState("");
  const [guarantorAddress, setGuarantorAddress] = useState("");

  const [consentTerms, setConsentTerms] = useState(false);
  const [consentTruth, setConsentTruth] = useState(false);
  /** Bank PDF password — used only in the browser to unlock before upload; never stored. */
  const [statementPdfPassword, setStatementPdfPassword] = useState("");

  const [amount, setAmount] = useState("");
  const [durationDays, setDurationDays] = useState(String(MAX_LOAN_DAYS));
  const [proposedRepay, setProposedRepay] = useState("");
  const [confirmLoan, setConfirmLoan] = useState(false);
  const [bankAccount, setBankAccount] = useState<{ bankName: string; accountNumber: string; accountName: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submittedKyc, setSubmittedKyc] = useState(false);

  const level: VerificationLevel = borrower?.verificationLevel ?? "none";
  const isEliteLoan = level === "elite";
  const canNegotiateLoan = canNegotiateRates(level);
  const maxLimit = borrower?.loanLimit ?? 0;
  const expressSchedule = borrower?.terms.expressRateSchedule ?? [];
  const maxDays = maxLoanDaysForLevel(level);
  const days = clampLoanDaysForLevel(level, Number(durationDays) || maxDays);

  const expressTotal = useMemo(() => {
    const n = Number(amount) || 0;
    if (!n) return 0;
    return expressRepayAmount(n, expressSchedule, days);
  }, [amount, expressSchedule, days]);

  const totalRepayable = canNegotiateLoan
    ? Number(proposedRepay) || 0
    : expressTotal;

  const dueDateIso = useMemo(
    () => new Date(Date.now() + days * 86400000).toISOString(),
    [days],
  );

  const repayByDate = useMemo(() => localDatePlusDays(new Date(), days), [days]);
  const minRepayDate = useMemo(() => localDatePlusDays(new Date(), MIN_LOAN_DAYS), []);
  const maxRepayDate = useMemo(() => localDatePlusDays(new Date(), maxDays), [maxDays]);

  const bankName = nigerianBanks.find((x) => x.code === bankCode)?.name ?? "";

  const socialCount = [socialInstagram, socialFacebook, socialTwitter, socialYoutube]
    .map((s) => s.trim())
    .filter(Boolean).length;

  const anyUploading = Object.values(uploadStatus).some((s) => s === "uploading");

  // Restore draft / approved Express KYC once — verify storage paths before showing "Uploaded"
  useEffect(() => {
    if (!borrower || (borrower.kycStatus === "approved" && !isVerifiedUpgrade) || draftLoaded.current) return;
    draftLoaded.current = true;

    const applyVerifiedDocs = (verified: DocMap) => {
      const statuses: Partial<Record<DocType, UploadStatus>> = {};
      const kept: DocMap = {};
      for (const t of DOC_TYPES) {
        if (verified[t]?.path) {
          kept[t] = verified[t] as UploadedKycDoc;
          statuses[t] = "done";
        }
      }
      setDocs(kept);
      setUploadStatus(statuses);
      return Object.keys(kept).length;
    };

    const seedFromApprovedKyc = () => {
      if (!isVerifiedUpgrade || !borrower.kyc) return;
      const k = borrower.kyc;
      if (k.dateOfBirth) setDateOfBirth(k.dateOfBirth);
      if (k.houseAddress) setHouseAddress(k.houseAddress);
      if (k.officeAddress) setOfficeAddress(k.officeAddress);
      if (k.workplaceName) setWorkplaceName(k.workplaceName);
      if (k.salaryAmount != null) setSalaryAmount(String(k.salaryAmount));
      if (k.socialInstagram) setSocialInstagram(k.socialInstagram);
      if (k.socialFacebook) setSocialFacebook(k.socialFacebook);
      if (k.socialTwitter) setSocialTwitter(k.socialTwitter);
      if (k.socialYoutube) setSocialYoutube(k.socialYoutube);
      if (k.loanPurpose) setLoanPurpose(k.loanPurpose);
      if (k.repaymentPlan) setRepaymentPlan(k.repaymentPlan);
      if (k.bvn) setBvn(k.bvn);
      if (k.nin) setNin(k.nin);
    };

    seedFromApprovedKyc();

    const d = loadDraft(borrower.id);
    const approvedDocs = isVerifiedUpgrade ? docsFromApprovedKyc(borrower.kyc?.documents) : {};
    const draftDocs = d?.docs ?? {};
    // Draft uploads win over older approved paths (user may have replaced a file).
    const mergedRaw: DocMap = { ...approvedDocs, ...draftDocs };

    if (d) {
      if (!isVerifiedUpgrade) {
        setStep(d.step <= 4 ? d.step : 0);
        setTargetTier(d.targetTier ?? null);
      }
      if (d.dateOfBirth) setDateOfBirth(d.dateOfBirth);
      if (d.houseAddress) setHouseAddress(d.houseAddress);
      if (d.officeAddress) setOfficeAddress(d.officeAddress);
      if (d.workplaceName) setWorkplaceName(d.workplaceName);
      if (d.salaryAmount) setSalaryAmount(d.salaryAmount);
      if (d.socialInstagram) setSocialInstagram(d.socialInstagram);
      if (d.socialFacebook) setSocialFacebook(d.socialFacebook);
      if (d.socialTwitter) setSocialTwitter(d.socialTwitter);
      if (d.socialYoutube) setSocialYoutube(d.socialYoutube);
      if (d.loanPurpose) setLoanPurpose(d.loanPurpose);
      if (d.repaymentPlan) setRepaymentPlan(d.repaymentPlan);
      if (d.bvn) setBvn(d.bvn);
      if (d.nin) setNin(d.nin);
      if (d.bankCode) setBankCode(d.bankCode);
      if (d.accountNumber) setAccountNumber(d.accountNumber);
      if (d.accountName) setAccountName(d.accountName);
      if (d.nokName) setNokName(d.nokName);
      if (d.nokRelationship) setNokRelationship(d.nokRelationship);
      if (d.nokPhone) setNokPhone(d.nokPhone);
      if (d.nokAddress) setNokAddress(d.nokAddress);
      if (d.guarantorName) setGuarantorName(d.guarantorName);
      if (d.guarantorRelationship) setGuarantorRelationship(d.guarantorRelationship);
      if (d.guarantorPhone) setGuarantorPhone(d.guarantorPhone);
      if (d.guarantorAddress) setGuarantorAddress(d.guarantorAddress);
      setConsentTerms(Boolean(d.consentTerms));
      setConsentTruth(Boolean(d.consentTruth));
    }

    const rawCount = Object.values(mergedRaw).filter((x) => x?.path).length;
    if (rawCount === 0) {
      setDocs({});
      setUploadStatus({});
      setDraftReady(true);
      return;
    }

    let cancelled = false;
    void filterExistingKycDocs(mergedRaw).then((verified) => {
      if (cancelled) return;
      const keptCount = applyVerifiedDocs(verified as DocMap);
      if (keptCount < rawCount) {
        setError(
          keptCount === 0
            ? "Saved documents were not found in storage. Please re-upload all files."
            : `${rawCount - keptCount} saved document(s) were missing from storage. Please re-upload them.`,
        );
      }
      setDraftReady(true);
    }).catch(() => {
      if (cancelled) return;
      setDocs({});
      setUploadStatus({});
      setError("Could not verify saved documents. Please re-upload your files.");
      setDraftReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [borrower, isVerifiedUpgrade]);

  // Persist draft (wait until storage verification finishes so we don't wipe real paths)
  useEffect(() => {
    if (!borrower || (borrower.kycStatus === "approved" && !isVerifiedUpgrade) || submittedKyc || !draftReady) return;
    saveDraft(borrower.id, {
      step,
      targetTier,
      dateOfBirth,
      houseAddress,
      officeAddress,
      workplaceName,
      salaryAmount,
      socialInstagram,
      socialFacebook,
      socialTwitter,
      socialYoutube,
      loanPurpose,
      repaymentPlan,
      bvn,
      nin,
      bankCode,
      accountNumber,
      accountName,
      nokName,
      nokRelationship,
      nokPhone,
      nokAddress,
      guarantorName,
      guarantorRelationship,
      guarantorPhone,
      guarantorAddress,
      docs,
      consentTerms,
      consentTruth,
    });
  }, [
    borrower,
    isVerifiedUpgrade,
    submittedKyc,
    draftReady,
    step,
    targetTier,
    dateOfBirth,
    houseAddress,
    officeAddress,
    workplaceName,
    salaryAmount,
    socialInstagram,
    socialFacebook,
    socialTwitter,
    socialYoutube,
    loanPurpose,
    repaymentPlan,
    bvn,
    nin,
    bankCode,
    accountNumber,
    accountName,
    nokName,
    nokRelationship,
    nokPhone,
    nokAddress,
    guarantorName,
    guarantorRelationship,
    guarantorPhone,
    guarantorAddress,
    docs,
    consentTerms,
    consentTruth,
  ]);

  useEffect(() => {
    if (!borrower || borrower.kycStatus !== "approved") return;
    fetchPrimaryBankAccount(borrower.id)
      .then((ba) => {
        if (!ba) return;
        setBankAccount({ bankName: ba.bankName, accountNumber: ba.accountNumber, accountName: ba.accountName });
        // Prefill the KYC bank fields too, so a Verified upgrade doesn't wipe the saved account.
        if (isVerifiedUpgrade) {
          setBankCode(ba.bankCode);
          setAccountNumber(ba.accountNumber);
          setAccountName(ba.accountName);
        }
      })
      .catch(() => {});
  }, [borrower, isVerifiedUpgrade]);

  // Live BVN uniqueness check once 11 digits are entered
  useEffect(() => {
    if (!borrower || borrower.kycStatus === "approved") return;
    const digits = bvn.replace(/\D/g, "");
    if (digits.length !== 11) {
      setBvnStatus("idle");
      return;
    }
    let cancelled = false;
    setBvnStatus("checking");
    const t = window.setTimeout(() => {
      isKycIdentifierTaken("bvn", digits, borrower.id)
        .then((taken) => {
          if (cancelled) return;
          setBvnStatus(taken ? "taken" : "ok");
          if (taken) setError("This BVN is already registered on another account.");
        })
        .catch(() => {
          if (!cancelled) setBvnStatus("idle");
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [bvn, borrower]);

  // Live NIN uniqueness check once 11 digits are entered
  useEffect(() => {
    if (!borrower || borrower.kycStatus === "approved") return;
    const digits = nin.replace(/\D/g, "");
    if (digits.length !== 11) {
      setNinStatus("idle");
      return;
    }
    let cancelled = false;
    setNinStatus("checking");
    const t = window.setTimeout(() => {
      isKycIdentifierTaken("nin", digits, borrower.id)
        .then((taken) => {
          if (cancelled) return;
          setNinStatus(taken ? "taken" : "ok");
          if (taken) setError("This NIN is already registered on another account.");
        })
        .catch(() => {
          if (!cancelled) setNinStatus("idle");
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [nin, borrower]);

  if (!borrower) return null;
  const b = borrower;

  if (b.kycStatus === "pending" && !submittedKyc) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Card>
          <Badge tone="warning">Under review</Badge>
          <h1 className="font-display text-3xl mt-3">
            {isVerifiedUpgradePending(b) ? "Verified upgrade under review" : "Your KYC is under review"}
          </h1>
          <p className="mt-3 text-muted-foreground">
            {isVerifiedUpgradePending(b)
              ? "You already applied to upgrade to Verified. You can't submit again until we approve or decline this request."
              : "Our team will verify your details and set your loan limit. You'll be notified as soon as possible."}
          </p>
          <div className="mt-6">
            <Button variant="gold" asChild><Link to="/borrower">Back to dashboard</Link></Button>
          </div>
        </Card>
      </div>
    );
  }

  // Apply for loan (default): do not open KYC wizard — prompt only
  if (b.kycStatus !== "approved" && flow !== "kyc") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Card>
          <Badge tone="warning">KYC required</Badge>
          <h1 className="font-display text-3xl mt-3">Complete KYC before applying</h1>
          <p className="mt-3 text-muted-foreground">
            {b.kycStatus === "declined"
              ? "Your previous KYC was declined. Please resubmit your verification to unlock loan applications."
              : "You need to verify your identity first. Once approved, you can request a loan from this page."}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button variant="gold" asChild>
              <Link to="/borrower/apply" search={{ flow: "kyc" }}>
                {b.kycStatus === "declined" ? "Resubmit KYC" : "Start KYC"}
              </Link>
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/borrower">Back to dashboard</Link>
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Already at the top tier and opened the KYC flow — nothing further to verify.
  if (b.kycStatus === "approved" && flow === "kyc" && b.verificationLevel === "verified") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Card>
          <Badge tone="success">Verified · {TIER_LABEL.verified}</Badge>
          <h1 className="font-display text-3xl mt-3">You're already verified</h1>
          <p className="mt-3 text-muted-foreground">
            Your Verified KYC is approved. You can request a loan whenever you're ready.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button variant="gold" asChild>
              <Link to="/borrower/apply" search={{ flow: "loan" }}>Request a loan</Link>
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/borrower">Back to dashboard</Link>
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const showLoanForm = flow !== "kyc";

  const onPickFile = async (docType: DocType, file: File | null) => {
    if (!file) return;
    setError(null);
    setUploadStatus((s) => ({ ...s, [docType]: "uploading" }));
    setUploadErrors((e) => ({ ...e, [docType]: undefined }));
    try {
      let toUpload = file;
      if (docType === "bank_statement") {
        toUpload = await unlockPdfFileIfNeeded(file, statementPdfPassword);
      }
      const uploaded = await uploadKycDocNow(b.id, toUpload, docType);
      setDocs((d) => ({ ...d, [docType]: uploaded }));
      setUploadStatus((s) => ({ ...s, [docType]: "done" }));
    } catch (e) {
      setUploadStatus((s) => ({ ...s, [docType]: "error" }));
      const msg = e instanceof Error ? e.message : "Upload failed";
      setUploadErrors((err) => ({ ...err, [docType]: msg }));
      setError(`Could not upload ${docType.replace(/_/g, " ")}: ${msg}`);
    }
  };

  const onAccountBlur = async () => {
    const digits = accountNumber.replace(/\D/g, "");
    if (digits.length < 10 || !bankCode) return;
    setVerifyingBank(true);
    setError(null);
    try {
      const data = await verifyBankAccount(digits, bankCode);
      const name = data.account_name || data.data?.account_name || "";
      if (!name) throw new Error("Could not resolve account name.");
      setAccountName(name);
    } catch (e) {
      setAccountName("");
      setError(e instanceof Error ? e.message : "Bank verification failed.");
    } finally {
      setVerifyingBank(false);
    }
  };

  const docReady = (t: DocType) => Boolean(docs[t]?.path) && uploadStatus[t] !== "uploading";

  const next = () => {
    setError(null);
    if (anyUploading) return setError("Please wait for uploads to finish.");

    if (step === 0) {
      if (!dateOfBirth) return setError("Please enter your date of birth.");
      if (!houseAddress.trim()) return setError("Please enter your home address.");
      if (!officeAddress.trim()) return setError("Please enter your office/business address.");
      if (!workplaceName.trim()) return setError("Please enter your workplace or business name.");
      if (!salaryAmount || Number(salaryAmount) <= 0) return setError("Please enter a valid salary amount.");
      if (socialCount < 2) return setError("Please provide at least 2 social media usernames.");
      if (!loanPurpose.trim()) return setError("Please describe the purpose of the loan.");
      if (!repaymentPlan.trim()) return setError("Please describe how you intend to repay.");
      setStep(1);
      return;
    }

    if (step === 1) {
      if (bvn.replace(/\D/g, "").length < 11) {
        return setError("Enter a valid 11-digit BVN.");
      }
      if (nin.replace(/\D/g, "").length < 11) {
        return setError("Enter a valid 11-digit NIN.");
      }
      if (bvnStatus === "checking" || ninStatus === "checking") {
        return setError("Please wait while we check your BVN/NIN.");
      }
      if (bvnStatus === "taken") {
        return setError("This BVN is already registered on another account.");
      }
      if (ninStatus === "taken") {
        return setError("This NIN is already registered on another account.");
      }
      for (const t of EXPRESS_DOC_TYPES) {
        if (!docReady(t)) return setError(`Please upload ${t.replace(/_/g, " ")} and wait until it finishes.`);
      }
      if (!bankCode) return setError("Select your bank.");
      if (accountNumber.replace(/\D/g, "").length < 10) return setError("Enter a valid 10-digit account number.");
      if (!accountName) return setError("Verify your account number first.");
      setStep(2);
      return;
    }

    if (step === 3) {
      for (const t of EXPRESS_DOC_TYPES) {
        if (!docReady(t)) {
          return setError(
            isVerifiedUpgrade
              ? `Missing ${t.replace(/_/g, " ")} from your Express KYC. Re-upload it below.`
              : `Please upload ${t.replace(/_/g, " ")} and wait until it finishes.`,
          );
        }
      }
      if (!docReady("office_id")) return setError("Please upload your office / work ID (required for Verified).");
      if (!docReady("salary_evidence")) return setError("Please upload salary evidence (required for Verified).");
      if (!docReady("bank_statement")) return setError("Please upload your 3-month bank statement.");
      if (!nokName.trim()) return setError("Please enter next of kin full name.");
      if (!nokRelationship) return setError("Select next of kin relationship.");
      if (nokPhone.replace(/\D/g, "").length < 10) return setError("Enter a valid next of kin phone number.");
      if (!nokAddress.trim()) return setError("Please enter next of kin home address.");
      if (!docReady("nok_id")) return setError("Please upload next of kin valid ID.");
      if (!guarantorName.trim()) return setError("Please enter guarantor full name.");
      if (!guarantorRelationship.trim()) return setError("Please enter your relationship with the guarantor.");
      if (guarantorPhone.replace(/\D/g, "").length < 10) return setError("Enter a valid guarantor phone number.");
      if (!guarantorAddress.trim()) return setError("Please enter guarantor home address.");
      if (!docReady("guarantor_id")) return setError("Please upload guarantor valid ID.");
      setStep(4);
      return;
    }

    if (step === 4) {
      if (!consentTerms || !consentTruth) {
        return setError("Please accept both consent statements to submit.");
      }
      void submitKycFlow();
    }
  };

  const goBack = () => {
    setError(null);
    if (step === 4) {
      setStep(targetTier === "verified" ? 3 : 2);
      return;
    }
    if (step === 3) {
      if (isVerifiedUpgrade) return;
      setStep(2);
      return;
    }
    if (step === 2) {
      setStep(1);
      return;
    }
    if (step === 1) {
      setStep(0);
    }
  };

  const submitKycFlow = async () => {
    const tier: Tier = targetTier ?? "express";
    const requiredTypes: readonly DocType[] =
      tier === "verified"
        ? ([...EXPRESS_DOC_TYPES, ...EXPRESS_OPTIONAL_DOC_TYPES, ...VERIFIED_EXTRA_DOC_TYPES] as const)
        : EXPRESS_DOC_TYPES;
    for (const t of requiredTypes) {
      if (!docs[t]?.path) {
        setError(`Missing ${t.replace(/_/g, " ")}. Go back and re-upload it.`);
        return;
      }
    }
    const documents = DOC_TYPES.map((t) => docs[t]).filter(Boolean) as UploadedKycDoc[];
    setLoading(true);
    setError(null);
    try {
      await submitKyc({
        userId: b.id,
        verificationLevel: tier,
        bvn,
        nin,
        dateOfBirth,
        houseAddress: houseAddress.trim(),
        officeAddress: officeAddress.trim(),
        workplaceName: workplaceName.trim(),
        salaryAmount: Number(salaryAmount),
        loanPurpose: loanPurpose.trim(),
        repaymentPlan: repaymentPlan.trim(),
        socialInstagram: socialInstagram.trim(),
        socialFacebook: socialFacebook.trim(),
        socialTwitter: socialTwitter.trim(),
        socialYoutube: socialYoutube.trim(),
        nokName: tier === "verified" ? nokName.trim() : "",
        nokRelationship: tier === "verified" ? nokRelationship : "",
        nokPhone: tier === "verified" ? nokPhone.trim() : "",
        nokAddress: tier === "verified" ? nokAddress.trim() : "",
        guarantorName: tier === "verified" ? guarantorName.trim() : "",
        guarantorRelationship: tier === "verified" ? guarantorRelationship.trim() : "",
        guarantorPhone: tier === "verified" ? guarantorPhone.trim() : "",
        guarantorAddress: tier === "verified" ? guarantorAddress.trim() : "",
        documents,
        bankCode,
        bankName,
        accountNumber: accountNumber.replace(/\D/g, ""),
        accountName,
      });
      clearDraft(b.id);
      await refreshBorrower();
      setSubmittedKyc(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const prepareConfirm = async () => {
    setError(null);
    const n = Number(amount);
    if (!n || n <= 0) return setError("Enter a valid amount.");
    if (n > maxLimit) return setError(`Amount exceeds your limit of ${formatNaira(maxLimit)}.`);
    if (days < MIN_LOAN_DAYS || days > maxDays) {
      return setError(`Duration must be between ${MIN_LOAN_DAYS} and ${maxDays} days.`);
    }
    if (canNegotiateLoan) {
      const repay = Number(proposedRepay);
      if (!repay || repay < n) {
        return setError("Enter how much you would like to pay back (must be at least the amount borrowed).");
      }
    }

    if (!bankAccount) {
      if (!bankCode) return setError("Select your bank for disbursement.");
      if (accountNumber.replace(/\D/g, "").length < 10) return setError("Enter a valid 10-digit account number.");
      if (!accountName.trim()) return setError("Verify your account name first (blur the account number field).");
      setLoading(true);
      try {
        const ba = await saveDisbursementBankAccount({
          userId: b.id,
          bankName,
          bankCode,
          accountNumber: accountNumber.replace(/\D/g, ""),
          accountName,
        });
        setBankAccount({ bankName: ba.bankName, accountNumber: ba.accountNumber, accountName: ba.accountName });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not save bank account.");
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    setConfirmLoan(true);
  };

  const submitLoan = async () => {
    setError(null);
    const n = Number(amount);
    setLoading(true);
    try {
      await createLoanApplication({
        borrowerId: b.id,
        amount: n,
        durationDays: days,
        verificationLevel: level,
        expressRateSchedule: expressSchedule,
        ...(canNegotiateLoan ? { proposedRepay: Number(proposedRepay) } : {}),
      });
      await refreshBorrower();
      nav({ to: "/borrower" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Loan request failed.");
    } finally {
      setLoading(false);
    }
  };

  if (submittedKyc) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Card>
          <Badge tone="warning">Submitted</Badge>
          <h1 className="font-display text-3xl mt-3">
            {targetTier === "verified" || isVerifiedUpgrade
              ? "Verified upgrade under review"
              : "Your KYC is under review"}
          </h1>
          <p className="mt-3 text-muted-foreground">
            {targetTier === "verified" || isVerifiedUpgrade
              ? "You can't apply again until we approve or decline this upgrade request. We'll notify you when a decision is made."
              : "Our team will verify your details and set your loan limit. You'll be notified as soon as possible."}
          </p>
          <div className="mt-6 flex gap-3">
            <Button variant="gold" asChild><Link to="/borrower">Back to dashboard</Link></Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {showLoanForm ? "Request a loan" : isVerifiedUpgrade ? "Upgrade verification" : "Identity verification"}
        </div>
        <h1 className="font-display text-4xl mt-1">
          {showLoanForm ? "Application" : isVerifiedUpgrade ? "Upgrade to Verified" : "Complete KYC"}
        </h1>
      </div>

      {isVerifiedUpgrade && !showLoanForm && (
        <div className="mb-6">
          <Badge tone="gold">Upgrading from {TIER_LABEL.express}</Badge>
        </div>
      )}

      {!showLoanForm && !isVerifiedUpgrade && (
        <div className="mb-8 grid grid-cols-4 gap-2">
          {KYC_STEPS.map((label, i) => {
            const idx = stepperIndex(step);
            const done = i < idx;
            const active = i === idx;
            return (
              <div key={label} className="flex flex-col items-center text-center">
                <div className={`h-9 w-9 rounded-full grid place-items-center text-sm font-medium border ${
                  done ? "bg-gradient-gold text-navy-deep border-transparent shadow-gold"
                  : active ? "border-gold text-gold" : "border-border text-muted-foreground"
                }`}>{i + 1}</div>
                <div className={`mt-2 text-[10px] uppercase tracking-wider ${active ? "text-gold" : "text-muted-foreground"}`}>
                  {label}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Card>
        <ErrorBanner message={error} />
        {!draftReady && !showLoanForm && (
          <p className="mb-4 text-sm text-muted-foreground">Verifying saved documents…</p>
        )}

        {!showLoanForm ? (
          <>
            {step === 0 && (
              <div className="space-y-4">
                <h2 className="font-display text-xl">About you</h2>
                <p className="text-sm text-muted-foreground">
                  Profile, employment, socials, and loan intent.
                </p>
                <Field label="Full name">
                  <Input value={b.name} readOnly className="opacity-80" />
                </Field>
                <Field label="Email">
                  <Input value={b.email} readOnly className="opacity-80" />
                </Field>
                <Field label="Phone">
                  <Input value={b.phone} readOnly className="opacity-80" />
                </Field>
                <Field label="Date of birth">
                  <Input type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />
                </Field>
                <Field label="Home address">
                  <Input value={houseAddress} onChange={(e) => setHouseAddress(e.target.value)} placeholder="Street, city, state" />
                </Field>
                <Field label="Office / business address">
                  <Input value={officeAddress} onChange={(e) => setOfficeAddress(e.target.value)} placeholder="Work address" />
                </Field>
                <Field label="Place of work / business">
                  <Input value={workplaceName} onChange={(e) => setWorkplaceName(e.target.value)} placeholder="Company or business name" />
                </Field>
                <Field label="Salary amount (NGN)">
                  <Input
                    type="number"
                    value={salaryAmount}
                    onChange={(e) => setSalaryAmount(e.target.value)}
                    placeholder="e.g. 350000"
                    inputMode="numeric"
                  />
                </Field>

                <h3 className="font-display text-lg pt-2">Social profiles</h3>
                <p className="text-sm text-muted-foreground">At least 2 platforms required.</p>
                <Field label="Instagram username">
                  <Input value={socialInstagram} onChange={(e) => setSocialInstagram(e.target.value)} placeholder="@username" />
                </Field>
                <Field label="Facebook username">
                  <Input value={socialFacebook} onChange={(e) => setSocialFacebook(e.target.value)} placeholder="username or profile URL" />
                </Field>
                <Field label="Twitter / X username">
                  <Input value={socialTwitter} onChange={(e) => setSocialTwitter(e.target.value)} placeholder="@username" />
                </Field>
                <Field label="YouTube username">
                  <Input value={socialYoutube} onChange={(e) => setSocialYoutube(e.target.value)} placeholder="channel or handle" />
                </Field>
                <p className="text-xs text-muted-foreground">{socialCount} of 2 required filled</p>

                <h3 className="font-display text-lg pt-2">Loan intent</h3>
                <Field label="Purpose of loan">
                  <textarea
                    className={textareaClass}
                    value={loanPurpose}
                    onChange={(e) => setLoanPurpose(e.target.value)}
                    placeholder="What will the loan be used for?"
                  />
                </Field>
                <Field label="How you intend to repay">
                  <textarea
                    className={textareaClass}
                    value={repaymentPlan}
                    onChange={(e) => setRepaymentPlan(e.target.value)}
                    placeholder="e.g. From monthly salary on payday"
                  />
                </Field>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <h2 className="font-display text-xl">{TIER_LABEL.express} documents & bank</h2>
                <p className="text-sm text-muted-foreground">
                  Files upload as soon as you select them. You can leave and come back — progress is saved.
                </p>
                <Field
                  label="BVN"
                  error={bvnStatus === "taken" ? "Already registered on another account" : undefined}
                  hint={
                    bvnStatus === "checking" ? "Checking…"
                      : bvnStatus === "ok" ? "BVN available"
                        : "Required"
                  }
                >
                  <Input
                    value={bvn}
                    onChange={(e) => {
                      setBvn(e.target.value.replace(/\D/g, "").slice(0, 11));
                      setError(null);
                    }}
                    placeholder="11-digit BVN"
                    inputMode="numeric"
                  />
                </Field>
                <Field
                  label="NIN"
                  error={ninStatus === "taken" ? "Already registered on another account" : undefined}
                  hint={
                    ninStatus === "checking" ? "Checking…"
                      : ninStatus === "ok" ? "NIN available"
                        : "Required"
                  }
                >
                  <Input
                    value={nin}
                    onChange={(e) => {
                      setNin(e.target.value.replace(/\D/g, "").slice(0, 11));
                      setError(null);
                    }}
                    placeholder="11-digit NIN"
                    inputMode="numeric"
                  />
                </Field>
                <Field label="NIN card (front)">
                  <DocUpload docType="nin_front" doc={docs.nin_front} status={uploadStatus.nin_front} error={uploadErrors.nin_front} accept="image/*,application/pdf" onPick={onPickFile} />
                </Field>
                <Field label="Selfie / passport photo">
                  <DocUpload docType="selfie" doc={docs.selfie} status={uploadStatus.selfie} error={uploadErrors.selfie} accept="image/*" onPick={onPickFile} />
                </Field>
                <Field label="Utility bill">
                  <DocUpload docType="utility_bill" doc={docs.utility_bill} status={uploadStatus.utility_bill} error={uploadErrors.utility_bill} accept="image/*,application/pdf" onPick={onPickFile} />
                </Field>
                <Field label="3-month bank statement (PDF)" hint="Required for Express">
                  <DocUpload docType="bank_statement" doc={docs.bank_statement} status={uploadStatus.bank_statement} error={uploadErrors.bank_statement} accept="application/pdf" onPick={onPickFile} />
                  <BankStatementPasswordTip
                    password={statementPdfPassword}
                    onPasswordChange={setStatementPdfPassword}
                  />
                </Field>
                <Field label="Office / work ID card" hint="Optional for Express — required for Verified">
                  <DocUpload docType="office_id" doc={docs.office_id} status={uploadStatus.office_id} error={uploadErrors.office_id} accept="image/*,application/pdf" onPick={onPickFile} />
                </Field>
                <Field label="Evidence of salary" hint="Optional for Express — you do not need to prove a salary job. Required for Verified.">
                  <DocUpload docType="salary_evidence" doc={docs.salary_evidence} status={uploadStatus.salary_evidence} error={uploadErrors.salary_evidence} accept="image/*,application/pdf" onPick={onPickFile} />
                </Field>
                <Field label="Bank">
                  <BankSelect
                    banks={nigerianBanks}
                    value={bankCode}
                    loading={banksLoading}
                    onChange={(code) => {
                      setBankCode(code);
                      setAccountName("");
                    }}
                  />
                </Field>
                <Field label="Account number" hint={verifyingBank ? "Verifying…" : accountName ? `Account name: ${accountName}` : undefined}>
                  <Input
                    value={accountNumber}
                    onChange={(e) => { setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 10)); setAccountName(""); }}
                    onBlur={onAccountBlur}
                    placeholder="10-digit account number"
                    inputMode="numeric"
                  />
                </Field>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <h2 className="font-display text-xl">Choose your verification tier</h2>
                <div className="rounded-md border border-gold/30 bg-gold/5 p-4 text-sm text-muted-foreground">
                  You can submit for Express now, or continue to Verified for higher benefits.
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-md border border-border p-4 space-y-2">
                    <Badge tone="gold">{TIER_LABEL.express}</Badge>
                    <p className="text-sm text-muted-foreground">
                      Simple onboarding with a bank statement — no guarantor or salary-job proof required. Weekly pricing by duration; you see your total repayable, not a rate.
                    </p>
                  </div>
                  <div className="rounded-md border border-gold/40 bg-gold/5 p-4 space-y-2">
                    <Badge tone="gold">{TIER_LABEL.verified}</Badge>
                    <p className="text-sm text-muted-foreground">
                      Higher limits — negotiate your own interest rate. Requires a salary job, bank statement, next of kin, and a guarantor we can reach.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                {isVerifiedUpgrade && (
                  <div className="space-y-4 rounded-md border border-border p-4">
                    <h2 className="font-display text-xl">Express documents on file</h2>
                    <p className="text-sm text-muted-foreground">
                      These were submitted with your Express KYC. Re-upload only if a file shows as missing.
                    </p>
                    <Field label="NIN card (front)">
                      <DocUpload docType="nin_front" doc={docs.nin_front} status={uploadStatus.nin_front} error={uploadErrors.nin_front} accept="image/*,application/pdf" onPick={onPickFile} />
                    </Field>
                    <Field label="Selfie holding NIN">
                      <DocUpload docType="selfie" doc={docs.selfie} status={uploadStatus.selfie} error={uploadErrors.selfie} accept="image/*" onPick={onPickFile} />
                    </Field>
                    <Field label="Utility bill">
                      <DocUpload docType="utility_bill" doc={docs.utility_bill} status={uploadStatus.utility_bill} error={uploadErrors.utility_bill} accept="image/*,application/pdf" onPick={onPickFile} />
                    </Field>
                  </div>
                )}
                <div className="space-y-4">
                  <h2 className="font-display text-xl">{TIER_LABEL.verified} verification</h2>
                  <p className="text-sm text-muted-foreground">
                    Salary job proof, next of kin, and a reachable guarantor unlock higher limits and the ability to negotiate your own interest rate.
                  </p>
                  <div className="rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
                    Please note that next of kin and guarantors may be randomly contacted.
                  </div>
                  <Field label="Office / work ID card">
                    <DocUpload docType="office_id" doc={docs.office_id} status={uploadStatus.office_id} error={uploadErrors.office_id} accept="image/*,application/pdf" onPick={onPickFile} />
                  </Field>
                  <Field label="Evidence of salary">
                    <DocUpload docType="salary_evidence" doc={docs.salary_evidence} status={uploadStatus.salary_evidence} error={uploadErrors.salary_evidence} accept="image/*,application/pdf" onPick={onPickFile} />
                  </Field>
                  <Field label="3-month bank statement (PDF)" hint="Already required for Express — re-upload only if needed">
                    <DocUpload docType="bank_statement" doc={docs.bank_statement} status={uploadStatus.bank_statement} error={uploadErrors.bank_statement} accept="application/pdf" onPick={onPickFile} />
                    <BankStatementPasswordTip
                      password={statementPdfPassword}
                      onPasswordChange={setStatementPdfPassword}
                    />
                  </Field>
                </div>
                <div className="border-t border-border pt-6 space-y-4">
                  <h3 className="font-display text-lg">Next of kin</h3>
                  <p className="text-sm text-muted-foreground">Must be father, mother, or spouse.</p>
                  <Field label="Full name">
                    <Input value={nokName} onChange={(e) => setNokName(e.target.value)} />
                  </Field>
                  <Field label="Relationship">
                    <select value={nokRelationship} onChange={(e) => setNokRelationship(e.target.value)} className={selectClass}>
                      <option value="">Select</option>
                      {NOK_RELATIONSHIPS.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Phone number">
                    <Input value={nokPhone} onChange={(e) => setNokPhone(e.target.value)} placeholder="+234..." inputMode="tel" />
                  </Field>
                  <Field label="Home address">
                    <Input value={nokAddress} onChange={(e) => setNokAddress(e.target.value)} />
                  </Field>
                  <Field label="Valid ID of next of kin">
                    <DocUpload docType="nok_id" doc={docs.nok_id} status={uploadStatus.nok_id} error={uploadErrors.nok_id} accept="image/*,application/pdf" onPick={onPickFile} />
                  </Field>
                </div>
                <div className="border-t border-border pt-6 space-y-4">
                  <h3 className="font-display text-lg">Guarantor</h3>
                  <Field label="Full name">
                    <Input value={guarantorName} onChange={(e) => setGuarantorName(e.target.value)} />
                  </Field>
                  <Field label="Relationship to you">
                    <Input value={guarantorRelationship} onChange={(e) => setGuarantorRelationship(e.target.value)} placeholder="e.g. Colleague, family friend" />
                  </Field>
                  <Field label="Phone number">
                    <Input value={guarantorPhone} onChange={(e) => setGuarantorPhone(e.target.value)} placeholder="+234..." inputMode="tel" />
                  </Field>
                  <Field label="Home address">
                    <Input value={guarantorAddress} onChange={(e) => setGuarantorAddress(e.target.value)} />
                  </Field>
                  <Field label="Valid ID of guarantor">
                    <DocUpload docType="guarantor_id" doc={docs.guarantor_id} status={uploadStatus.guarantor_id} error={uploadErrors.guarantor_id} accept="image/*,application/pdf" onPick={onPickFile} />
                  </Field>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <h2 className="font-display text-xl">Review & submit</h2>
                <div className="rounded-md bg-accent/40 p-4 text-sm space-y-1.5">
                  <Row k="Tier" v={TIER_LABEL[targetTier ?? "express"]} />
                  <Row k="Name" v={b.name} />
                  <Row k="Date of birth" v={dateOfBirth || "—"} />
                  <Row k="Home" v={houseAddress || "—"} />
                  <Row k="Office" v={officeAddress || "—"} />
                  <Row k="Workplace" v={workplaceName || "—"} />
                  <Row k="Salary" v={formatNaira(Number(salaryAmount) || 0)} />
                  <Row k="Socials" v={`${socialCount} provided`} />
                  <Row k="Purpose" v={loanPurpose.slice(0, 80) + (loanPurpose.length > 80 ? "…" : "")} />
                  <Row k="Repayment" v={repaymentPlan.slice(0, 80) + (repaymentPlan.length > 80 ? "…" : "")} />
                  <Row k="BVN" v={bvn || "—"} />
                  <Row k="NIN" v={nin || "—"} />
                  <Row k="Bank" v={`${bankName || "—"} · ${accountNumber || "—"}`} />
                  <Row k="Documents" v={`${Object.keys(docs).length} uploaded`} />
                  {targetTier === "verified" && (
                    <>
                      <Row k="Next of kin" v={`${nokName} (${nokRelationship})`} />
                      <Row k="Guarantor" v={guarantorName} />
                    </>
                  )}
                </div>
                <p className="text-sm text-muted-foreground border border-border/60 rounded-md p-3">
                  Note: Filling this form does not 100% guarantee you will receive a loan. The information provided will be reviewed before a decision is made.
                </p>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentTerms}
                    onChange={(e) => setConsentTerms(e.target.checked)}
                    className="mt-1 accent-[var(--primary)]"
                  />
                  <span>
                    I have read and agree to the{" "}
                    <Link to="/terms" target="_blank" className="text-gold hover:underline">Terms &amp; Conditions</Link>
                    {", "}
                    <Link to="/privacy" target="_blank" className="text-gold hover:underline">Privacy Policy</Link>
                    {", and "}
                    <Link to="/responsible-lending" target="_blank" className="text-gold hover:underline">Responsible Lending Policy</Link>
                    .
                  </span>
                </label>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentTruth}
                    onChange={(e) => setConsentTruth(e.target.checked)}
                    className="mt-1 accent-[var(--primary)]"
                  />
                  <span>
                    I confirm that all information provided is accurate and truthful. Providing false information may result in permanent account suspension.
                  </span>
                </label>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between gap-3">
              {step === 2 ? (
                <>
                  <Button variant="ghost" onClick={() => setStep(1)} disabled={loading}>
                    Back
                  </Button>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      onClick={() => { setTargetTier("express"); setStep(4); }}
                    >
                      Submit for Express
                    </Button>
                    <Button
                      variant="gold"
                      onClick={() => { setTargetTier("verified"); setStep(3); }}
                    >
                      Continue to Verified
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={goBack}
                    disabled={loading || step === 0 || (step === 3 && isVerifiedUpgrade)}
                  >
                    Back
                  </Button>
                  <Button variant="gold" loading={loading || anyUploading || !draftReady} onClick={next} disabled={!draftReady}>
                    {step === 4 ? "Confirm & Submit" : "Continue"}
                  </Button>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <h2 className="font-display text-xl">{confirmLoan ? "Confirm loan" : "Loan request"}</h2>
            {confirmLoan ? (
              <div className="space-y-4">
                <div className="rounded-md bg-accent/40 p-4 text-sm space-y-1.5">
                  <Row k="Amount to borrow" v={formatNaira(Number(amount) || 0)} />
                  <Row k="Pay back in" v={formatDurationDays(days, { capAt30: !isEliteLoan })} />
                  <Row k="Total to repay" v={<span className="text-gold">{formatNaira(totalRepayable)}</span>} />
                  <Row k="Due date" v={formatDate(dueDateIso)} />
                </div>
                {bankAccount && (
                  <div className="rounded-md border border-border p-4 text-sm space-y-1.5">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Disburse to</div>
                    <Row k="Bank" v={bankAccount.bankName} />
                    <Row k="Account name" v={bankAccount.accountName} />
                    <Row k="Account number" v={bankAccount.accountNumber} />
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-5">
                <Field label={`How much would you like to borrow? (max ${formatNaira(maxLimit)})`}>
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="e.g. 15000"
                    inputMode="numeric"
                  />
                </Field>
                <Field
                  label="When would you like to pay back?"
                  hint={
                    isEliteLoan
                      ? `Pick a date within the next ${maxDays} days (Elite) · ${formatDurationDays(days, { capAt30: false })} from today`
                      : `Pick a date within the next ${maxDays} days · ${formatDurationDays(days)} from today`
                  }
                >
                  <Input
                    type="date"
                    min={minRepayDate}
                    max={maxRepayDate}
                    value={repayByDate}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (!raw) return;
                      const n = daysBetweenTodayAnd(raw);
                      setDurationDays(String(clampLoanDaysForLevel(level, n)));
                    }}
                  />
                </Field>

                {canNegotiateLoan ? (
                  <Field
                    label="How much would you like to pay back?"
                    hint="Required. We review each proposal — admin may accept, counter, or decline."
                  >
                    <Input
                      type="number"
                      value={proposedRepay}
                      onChange={(e) => setProposedRepay(e.target.value)}
                      placeholder={Number(amount) > 0 ? `e.g. ${Math.round(expressTotal || Number(amount) * 1.08)}` : "e.g. 108000"}
                      inputMode="numeric"
                    />
                  </Field>
                ) : (
                  <div className="rounded-md border border-border p-3 text-sm text-muted-foreground">
                    Your total repayable is calculated from your chosen duration. You will confirm the exact amount before submitting.
                  </div>
                )}

                <div className="rounded-md bg-accent/40 p-4 text-sm space-y-1.5">
                  <Row k="Amount to borrow" v={formatNaira(Number(amount) || 0)} />
                  <Row k="Pay back in" v={formatDurationDays(days, { capAt30: !isEliteLoan })} />
                  <Row k="Due date" v={formatDate(dueDateIso)} />
                  <Row
                    k="Total to repay"
                    v={<span className="text-gold">{formatNaira(canNegotiateLoan ? (Number(proposedRepay) || 0) : expressTotal)}</span>}
                  />
                </div>

                {!bankAccount ? (
                  <div className="space-y-3 rounded-md border border-warning/40 bg-warning/5 p-4">
                    <div className="text-sm text-warning">Add a bank account for disbursement before submitting.</div>
                    <Field label="Bank">
                      <BankSelect
                        banks={nigerianBanks}
                        value={bankCode}
                        loading={banksLoading}
                        onChange={(code) => {
                          setBankCode(code);
                          setAccountName("");
                        }}
                      />
                    </Field>
                    <Field label="Account number">
                      <Input
                        value={accountNumber}
                        onChange={(e) => { setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 10)); setAccountName(""); }}
                        onBlur={onAccountBlur}
                        inputMode="numeric"
                        placeholder="10 digits"
                      />
                      {verifyingBank && <p className="mt-1 text-xs text-muted-foreground">Verifying…</p>}
                      {accountName && <p className="mt-1 text-xs text-success">{accountName}</p>}
                    </Field>
                  </div>
                ) : (
                  <div className="rounded-md border border-border p-4 text-sm space-y-1.5">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Disburse to</div>
                    <Row k="Bank" v={bankAccount.bankName} />
                    <Row k="Account name" v={bankAccount.accountName} />
                    <Row k="Account number" v={bankAccount.accountNumber} />
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                onClick={() => setConfirmLoan(false)}
                disabled={!confirmLoan || loading}
              >
                Back
              </Button>
              {confirmLoan ? (
                <Button variant="gold" loading={loading} onClick={submitLoan}>
                  Confirm & Submit
                </Button>
              ) : (
                <Button variant="gold" loading={loading} onClick={prepareConfirm}>
                  Submit for approval
                </Button>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground shrink-0">{k}</span>
      <span className="text-right break-words">{v}</span>
    </div>
  );
}

function BankStatementPasswordTip({
  password,
  onPasswordChange,
}: {
  password: string;
  onPasswordChange: (v: string) => void;
}) {
  return (
    <div className="mt-3 space-y-2">
      <Field
        label="PDF password (if locked)"
        hint="Enter before uploading. Used only on your device to unlock the file — we never store it."
      >
        <PasswordInput
          autoComplete="off"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="e.g. account number or DOB"
        />
      </Field>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Banks like Access often password-protect statements. Enter that password here, then upload.
        If Print → Save as PDF still asks for a password, this field is the reliable fix — photos of long
        statements are not ideal for review.
      </p>
    </div>
  );
}

function DocUpload({
  docType,
  doc,
  status,
  error,
  accept,
  onPick,
}: {
  docType: DocType;
  doc?: UploadedKycDoc;
  status?: UploadStatus;
  error?: string;
  accept: string;
  onPick: (type: DocType, file: File | null) => void;
}) {
  const uploading = status === "uploading";
  const done = status === "done" && doc?.path;
  const failed = status === "error";

  return (
    <label className="block">
      <div className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
        done ? "border-gold/50 bg-gold/5"
          : failed ? "border-destructive/50 bg-destructive/5"
            : uploading ? "border-gold/30 bg-accent/20"
              : "border-border hover:border-gold/40 hover:bg-accent/30"
      }`}>
        {uploading ? (
          <>
            <div className="font-medium text-gold">Uploading…</div>
            <div className="text-xs text-muted-foreground mt-1">Please wait</div>
          </>
        ) : done ? (
          <>
            <div className="font-medium text-gold">{doc.name}</div>
            <div className="text-xs text-success mt-1">Uploaded · Click to replace</div>
          </>
        ) : failed ? (
          <>
            <div className="font-medium text-destructive">Upload failed</div>
            <div className="text-xs text-muted-foreground mt-1">{error || "Try again"}</div>
          </>
        ) : (
          <>
            <div className="text-sm">Click to upload</div>
            <div className="text-xs text-muted-foreground mt-1">{accept}</div>
          </>
        )}
      </div>
      <input
        type="file"
        accept={accept}
        className="hidden"
        disabled={uploading}
        onChange={(e) => {
          onPick(docType, e.target.files?.[0] ?? null);
          e.target.value = "";
        }}
      />
    </label>
  );
}
